/**
 * Front-of-house screens: title, lobby, how-to-play, pause.
 *
 * The lobby carries the most weight. Local multiplayer lives or dies on "how do I know my
 * controller is player 2" — so every seat shows its own device label, its own colour, its
 * own shape, and a live "press A to join" affordance that visibly changes the moment a pad
 * is picked up.
 */

import {
  ACTIONS, GAME_TAGLINE, GAME_TITLE, GAME_VERSION, PLAYER_SHORT, REGIONS, VP,
} from '../game/constants';
import { seedToWord } from '../game/rng';
import type { GameSettings, PlayerId } from '../game/types';
import { ease, INK, seatColor, TYPE, withAlpha } from './palette';
import {
  buttonGlyph, chip, hintBar, panel, paragraph, roundRect, seatBadge, text,
} from './widgets';
import { ACTION_ORDER } from './actionOrder';

/** A seat's lobby state, mirrored from the InputRouter's bindings. */
export interface LobbySeat {
  seat: PlayerId;
  /** 'human' once a device has claimed it, 'ai' if filled by a bot, 'open' if free. */
  status: 'human' | 'ai' | 'open';
  /** Device label, e.g. "Xbox Wireless Controller" or "Keyboard (WASD)". */
  device: string;
  connected: boolean;
  name: string;
}

export interface LobbyView {
  seats: LobbySeat[];
  settings: GameSettings;
  /** Which settings row the host is adjusting. */
  settingIndex: number;
  /** Any gamepad connected at all — drives the keyboard-fallback hint. */
  hasGamepad: boolean;
}

export type PauseItem = 'resume' | 'help' | 'audio' | 'restart' | 'quit';

export interface PauseView {
  index: number;
  items: PauseItem[];
  audio: { master: number; sfx: number; music: number; muted: boolean };
}

/** Settings rows the lobby exposes, in order. */
export const LOBBY_SETTINGS = ['rounds', 'difficulty', 'seed', 'clock'] as const;
export type LobbySetting = typeof LOBBY_SETTINGS[number];

export class Screens {
  private time = 0;

  update(dtMs: number): void {
    this.time += dtMs;
  }

  /* --------------------------------------------------------------------- title */

  drawTitle(ctx: CanvasRenderingContext2D, w: number, h: number): void {
    // Title lockup, with the four seat colours as an underline — the fastest possible
    // way to communicate "this is a four-player game" before a word is read.
    const cx = w / 2;
    const titleY = h * 0.34;
    const drift = Math.sin(this.time / 2600) * 5;

    text(ctx, GAME_TITLE, cx, titleY + drift, {
      size: Math.min(TYPE.hero * 1.35, w * 0.115), weight: 800, color: INK.text,
      align: 'center', baseline: 'middle', tracking: 12,
      shadow: 'rgba(120, 170, 255, 0.55)',
    });

    const barW = Math.min(560, w * 0.46);
    const segW = barW / 4;
    for (let seat = 0; seat < 4; seat++) {
      const pulse = ease.pulse(((this.time + seat * 320) % 2400) / 2400);
      ctx.save();
      ctx.globalAlpha = 0.55 + pulse * 0.45;
      ctx.fillStyle = seatColor(seat as PlayerId);
      ctx.fillRect(cx - barW / 2 + seat * segW, titleY + drift + 52, segW - 5, 5);
      ctx.restore();
    }

    text(ctx, GAME_TAGLINE, cx, titleY + drift + 84, {
      size: TYPE.heading, weight: 600, color: INK.textDim, align: 'center', baseline: 'middle',
      tracking: 5,
    });

    text(ctx, '4 PLAYERS · LOCAL · CONTROLLER READY · ~60 MINUTES', cx, titleY + drift + 126, {
      size: TYPE.label, weight: 700, color: INK.textFaint, align: 'center', baseline: 'middle',
      tracking: 3.4,
    });

    // Prompt.
    const promptPulse = ease.pulse((this.time % 1800) / 1800);
    ctx.save();
    ctx.globalAlpha = 0.55 + promptPulse * 0.45;
    const py = h * 0.72;
    buttonGlyph(ctx, 'a', cx - 92, py, 15);
    text(ctx, 'PRESS A OR SPACE TO BEGIN', cx - 62, py, {
      size: TYPE.body, weight: 700, color: INK.text, baseline: 'middle', tracking: 2.2,
    });
    ctx.restore();

    text(ctx, `v${GAME_VERSION}`, w - 24, h - 22, {
      size: TYPE.micro, weight: 600, color: INK.textFaint, align: 'right', baseline: 'middle',
    });
    hintBar(ctx, [{ button: 'y', label: 'How to play' }], 24, h - 22, { size: TYPE.micro });
  }

  /* --------------------------------------------------------------------- lobby */

  drawLobby(ctx: CanvasRenderingContext2D, view: LobbyView, w: number, h: number): void {
    text(ctx, 'CLAIM YOUR CHARTER', w / 2, h * 0.1, {
      size: TYPE.heading, weight: 800, color: INK.text, align: 'center', baseline: 'middle',
      tracking: 5,
    });
    text(
      ctx,
      view.hasGamepad
        ? 'Press A on a controller to take a seat. Empty seats are played by the Autarch AI.'
        : 'No controllers detected — keyboard schemes are assigned automatically. Press A / F / H / Numpad-0 to join.',
      w / 2, h * 0.1 + 34,
      { size: TYPE.label, weight: 500, color: INK.textDim, align: 'center', baseline: 'middle' },
    );

    // Seat cards.
    const gap = 18;
    const cardW = Math.min(292, (w * 0.88 - gap * 3) / 4);
    const totalW = cardW * 4 + gap * 3;
    const startX = w / 2 - totalW / 2;
    const cardY = h * 0.24;
    const cardH = h * 0.4;

    view.seats.forEach((seat) => {
      const x = startX + seat.seat * (cardW + gap);
      this.drawSeatCard(ctx, seat, x, cardY, cardW, cardH);
    });

    // Settings.
    this.drawLobbySettings(ctx, view, w / 2, cardY + cardH + 44, Math.min(720, w * 0.7));

    hintBar(
      ctx,
      [
        { button: 'a', label: 'Join' },
        { button: 'b', label: 'Leave seat' },
        { button: 'x', label: 'Adjust setting' },
        { button: 'start', label: 'Launch match' },
      ],
      w / 2 - 300, h - 40,
    );
  }

  private drawSeatCard(
    ctx: CanvasRenderingContext2D,
    seat: LobbySeat,
    x: number, y: number, w: number, h: number,
  ): void {
    const color = seatColor(seat.seat);
    const joined = seat.status === 'human';
    const pulse = ease.pulse((this.time % 1600) / 1600);

    panel(ctx, x, y, w, h, {
      fill: joined ? withAlpha(color, 0.16) : 'rgba(10, 12, 22, 0.86)',
      border: joined ? withAlpha(color, 0.9) : withAlpha(INK.edge, 0.8),
      borderWidth: joined ? 2.6 : 1.4,
      accent: color,
      accentWidth: 6,
      glow: joined ? withAlpha(color, 0.4) : undefined,
      chamfer: 18,
    });

    // Seat number + shape.
    seatBadge(ctx, seat.seat, x + w / 2, y + 58, 26, {
      label: PLAYER_SHORT[seat.seat],
      active: joined,
    });

    text(ctx, seat.name, x + w / 2, y + 108, {
      size: TYPE.label, weight: 800, color: joined ? INK.text : INK.textDim,
      align: 'center', baseline: 'middle', maxWidth: w - 30,
    });

    // Status.
    const statusY = y + h * 0.6;
    if (joined) {
      chip(ctx, seat.connected ? 'ready' : 'reconnecting', x + w / 2 - 34, statusY - 12, {
        color: seat.connected ? INK.good : INK.warn, filled: true, size: TYPE.micro, height: 24,
      });
      text(ctx, seat.device, x + w / 2, statusY + 32, {
        size: TYPE.micro, weight: 600, color: INK.textDim, align: 'center', baseline: 'middle',
        maxWidth: w - 26,
      });
    } else if (seat.status === 'ai') {
      chip(ctx, 'autarch AI', x + w / 2 - 42, statusY - 12, {
        color: INK.info, size: TYPE.micro, height: 24,
      });
      text(ctx, 'Will be played by the computer', x + w / 2, statusY + 32, {
        size: TYPE.micro, weight: 500, color: INK.textFaint, align: 'center', baseline: 'middle',
        maxWidth: w - 26,
      });
    } else {
      ctx.save();
      ctx.globalAlpha = 0.5 + pulse * 0.5;
      buttonGlyph(ctx, 'a', x + w / 2, statusY, 15);
      ctx.restore();
      text(ctx, 'PRESS TO JOIN', x + w / 2, statusY + 34, {
        size: TYPE.micro, weight: 700, color: INK.textDim, align: 'center', baseline: 'middle',
        tracking: 1.6,
      });
    }
  }

  private drawLobbySettings(
    ctx: CanvasRenderingContext2D,
    view: LobbyView,
    cx: number,
    y: number,
    w: number,
  ): void {
    const rowH = 34;
    const x = cx - w / 2;

    panel(ctx, x, y, w, rowH * LOBBY_SETTINGS.length + 20, {
      fill: 'rgba(10, 12, 22, 0.84)',
      border: INK.edge,
      chamfer: 14,
    });

    const labels: Record<LobbySetting, [string, string]> = {
      rounds: ['Rounds', `${view.settings.totalRounds}  (~${Math.round(view.settings.totalRounds * 7)} min)`],
      difficulty: ['Autarch skill', view.settings.aiDifficulty],
      seed: ['Galaxy', `${seedToWord(view.settings.seed)}  (#${view.settings.seed})`],
      clock: ['Round clock', view.settings.roundSeconds > 0
        ? `${Math.round(view.settings.roundSeconds / 60)} min advisory${view.settings.blitz ? ' · BLITZ' : ''}`
        : 'off'],
    };

    LOBBY_SETTINGS.forEach((key, index) => {
      const rowY = y + 18 + index * rowH;
      const selected = index === view.settingIndex;
      const [label, value] = labels[key];

      if (selected) {
        roundRect(ctx, x + 8, rowY - 12, w - 16, rowH - 4, 8);
        ctx.fillStyle = withAlpha(INK.info, 0.16);
        ctx.fill();
        ctx.strokeStyle = withAlpha(INK.info, 0.7);
        ctx.lineWidth = 1.4;
        ctx.stroke();
      }

      text(ctx, label, x + 26, rowY + 3, {
        size: TYPE.label, weight: 700, color: selected ? INK.text : INK.textDim,
        baseline: 'middle',
      });
      text(ctx, value, x + w - 26, rowY + 3, {
        size: TYPE.label, weight: 700, color: selected ? INK.info : INK.textDim,
        align: 'right', baseline: 'middle',
      });
      if (selected) {
        text(ctx, '‹', x + w * 0.62, rowY + 3, {
          size: TYPE.body, weight: 800, color: INK.info, align: 'center', baseline: 'middle',
        });
        text(ctx, '›', x + w * 0.66, rowY + 3, {
          size: TYPE.body, weight: 800, color: INK.info, align: 'center', baseline: 'middle',
        });
      }
    });
  }

  /* ---------------------------------------------------------------------- help */

  /**
   * How to play, on two pages. Deliberately dense — this replaces a rulebook, and a
   * couch game needs the rules reachable mid-match without a phone.
   */
  drawHelp(ctx: CanvasRenderingContext2D, page: number, w: number, h: number): void {
    const pageCount = 3;
    const clamped = ((page % pageCount) + pageCount) % pageCount;

    const cardW = Math.min(1020, w * 0.86);
    const x = w / 2 - cardW / 2;
    const y = h * 0.08;
    const cardH = h * 0.82;

    panel(ctx, x, y, cardW, cardH, {
      fill: 'rgba(9, 11, 20, 0.96)',
      border: INK.edgeStrong,
      borderWidth: 2,
      chamfer: 22,
    });

    const titles = ['HOW TO PLAY', 'ACTIONS', 'SCORING & CONTROLS'];
    text(ctx, titles[clamped]!, x + 36, y + 46, {
      size: TYPE.heading, weight: 800, color: INK.text, baseline: 'middle', tracking: 4,
    });
    text(ctx, `${clamped + 1} / ${pageCount}`, x + cardW - 36, y + 46, {
      size: TYPE.label, weight: 700, color: INK.textFaint, align: 'right', baseline: 'middle',
    });

    if (clamped === 0) this.drawHelpOverview(ctx, x + 36, y + 92, cardW - 72);
    else if (clamped === 1) this.drawHelpActions(ctx, x + 36, y + 92, cardW - 72);
    else this.drawHelpScoring(ctx, x + 36, y + 92, cardW - 72, cardH - 140);

    hintBar(
      ctx,
      [
        { button: 'lb', label: 'Previous' },
        { button: 'rb', label: 'Next' },
        { button: 'b', label: 'Close' },
      ],
      x + 36, y + cardH - 28,
    );
  }

  private drawHelpOverview(ctx: CanvasRenderingContext2D, x: number, y: number, w: number): void {
    const colW = (w - 40) / 2;

    let leftY = y;
    leftY += this.helpHeading(ctx, 'THE GOAL', x, leftY);
    leftY += paragraph(
      ctx,
      'Colonise a shared galaxy over eight rounds and finish with the most Victory Points. ' +
      'Points come from Colonies, technology, Charter contracts, relics, population, and ' +
      'holding majorities in the three regions.',
      x, leftY, colW, { size: TYPE.label, color: INK.textDim, lineHeight: 22 },
    ) + 22;

    leftY += this.helpHeading(ctx, 'A ROUND', x, leftY);
    const phases: [string, string][] = [
      ['Drift', 'A global event card flips. Turn order is recomputed — last place moves first.'],
      ['Production', 'Every holding pays out. Colonies pay double. Population grows, then eats.'],
      ['Command', 'Round-robin: one action each, looping until everyone has passed.'],
      ['Contest', 'Any system with two players’ fleets resolves as a sealed Fuel bid.'],
      ['Auction', 'On odd rounds, two Charter contracts go to a sealed Data bid.'],
      ['Upkeep', 'Over-cap resources are lost. Regions score on rounds 4 and 8.'],
    ];
    for (const [name, blurb] of phases) {
      text(ctx, name, x, leftY + 7, {
        size: TYPE.label, weight: 800, color: INK.info, baseline: 'middle',
      });
      leftY += paragraph(ctx, blurb, x + 92, leftY, colW - 92, {
        size: TYPE.micro + 1, color: INK.textDim, lineHeight: 17,
      }) + 10;
    }

    // Right column.
    const rx = x + colW + 40;
    let rightY = y;
    rightY += this.helpHeading(ctx, 'RESOURCES', rx, rightY);
    const resources: [string, string, string][] = [
      ['◆', 'Ore', 'Builds outposts, colonies, fleets and megastructures.'],
      ['▲', 'Fuel', 'Pays for long jumps, and is what you bid in a Contest.'],
      ['❋', 'Biomass', 'Founding colonies, and feeding your population.'],
      ['◈', 'Data', 'Buys technology, and is what you bid at auction.'],
    ];
    for (const [glyph, name, blurb] of resources) {
      text(ctx, glyph, rx, rightY + 8, { size: TYPE.body, color: INK.text, baseline: 'middle' });
      text(ctx, name, rx + 26, rightY + 8, {
        size: TYPE.label, weight: 800, color: INK.text, baseline: 'middle',
      });
      rightY += paragraph(ctx, blurb, rx + 100, rightY, colW - 100, {
        size: TYPE.micro + 1, color: INK.textDim, lineHeight: 17,
      }) + 12;
    }

    rightY += 12;
    rightY += this.helpHeading(ctx, 'THE THREE REGIONS', rx, rightY);
    for (const region of ['core', 'midRim', 'outerRim'] as const) {
      const def = REGIONS[region];
      chip(ctx, def.name, rx, rightY, { color: def.tint, size: TYPE.micro, height: 22 });
      text(
        ctx,
        `majority +${def.majorityVp} VP, runner-up +${def.runnerUpVp}`,
        rx + 132, rightY + 11,
        { size: TYPE.micro + 1, weight: 600, color: INK.textDim, baseline: 'middle' },
      );
      rightY += 30;
    }
    rightY += paragraph(
      ctx,
      'Regions score twice — after round 4 and after round 8 — and the award is banked, ' +
      'so losing ground later does not take the points back.',
      rx, rightY + 6, colW, { size: TYPE.micro + 1, color: INK.textFaint, lineHeight: 17 },
    );
  }

  private drawHelpActions(ctx: CanvasRenderingContext2D, x: number, y: number, w: number): void {
    paragraph(
      ctx,
      'You start each Command phase with 5 Command Points, plus 1 per 4 Population. ' +
      'Play passes one action at a time around the table until everybody has passed.',
      x, y, w, { size: TYPE.label, color: INK.textDim, lineHeight: 22 },
    );

    let rowY = y + 52;
    const rowH = 40;
    for (const kind of ACTION_ORDER) {
      const def = ACTIONS[kind];
      const cost: string[] = [];
      if (def.cost.ore) cost.push(`${def.cost.ore}◆`);
      if (def.cost.fuel) cost.push(`${def.cost.fuel}▲`);
      if (def.cost.biomass) cost.push(`${def.cost.biomass}❋`);
      if (def.cost.data) cost.push(`${def.cost.data}◈`);

      roundRect(ctx, x, rowY - 14, w, rowH - 6, 8);
      ctx.fillStyle = 'rgba(255,255,255,0.028)';
      ctx.fill();

      text(ctx, def.glyph, x + 20, rowY + 4, {
        size: TYPE.body, color: INK.text, align: 'center', baseline: 'middle',
      });
      text(ctx, def.name, x + 40, rowY + 4, {
        size: TYPE.label, weight: 800, color: INK.text, baseline: 'middle',
      });
      text(ctx, def.cp > 0 ? `${def.cp} CP` : '—', x + 190, rowY + 4, {
        size: TYPE.micro, weight: 800, color: INK.info, baseline: 'middle',
      });
      text(ctx, cost.join(' ') || '—', x + 246, rowY + 4, {
        size: TYPE.micro, weight: 700, color: INK.textDim, baseline: 'middle',
      });
      text(ctx, def.blurb, x + 340, rowY + 4, {
        size: TYPE.micro + 1, weight: 500, color: INK.textDim, baseline: 'middle',
        maxWidth: w - 356,
      });
      rowY += rowH;
    }
  }

  private drawHelpScoring(
    ctx: CanvasRenderingContext2D,
    x: number, y: number, w: number, h: number,
  ): void {
    const colW = (w - 40) / 2;

    let leftY = y;
    leftY += this.helpHeading(ctx, 'VICTORY POINTS', x, leftY);
    const scores: [string, string][] = [
      ['Outpost', `${VP.outpost} VP each`],
      ['Colony', `${VP.colony} VP (${VP.colonyPrime} at habitability 3)`],
      ['Technology', '1 / 2 / 3 VP by tier'],
      ['Charter contract', '3–5 VP if fulfilled'],
      ['Relic recovered', `${VP.relic} VP each`],
      ['Megastructure', `${VP.megastructure} VP`],
      ['Population', `1 VP per ${VP.populationPer}`],
      ['Region majority', 'up to 5 VP, twice per match'],
    ];
    for (const [label, value] of scores) {
      text(ctx, label, x, leftY + 8, {
        size: TYPE.label, weight: 700, color: INK.textDim, baseline: 'middle',
      });
      text(ctx, value, x + colW, leftY + 8, {
        size: TYPE.label, weight: 800, color: '#ffd76b', align: 'right', baseline: 'middle',
      });
      leftY += 30;
    }
    leftY += 8;
    paragraph(ctx, 'Ties break on Data, then Fuel, then Ore, then seat order.',
      x, leftY, colW, { size: TYPE.micro + 1, color: INK.textFaint, lineHeight: 17 });

    // Controls.
    const rx = x + colW + 40;
    let rightY = y;
    rightY += this.helpHeading(ctx, 'CONTROLLER', rx, rightY);
    const controls: [string, string][] = [
      ['a', 'Confirm / select'],
      ['b', 'Cancel / back'],
      ['x', 'Open the action menu'],
      ['y', 'Inspect the system under your cursor'],
      ['lb', 'Cycle your fleets'],
      ['rb', 'Cycle your colonies'],
      ['lt', 'Zoom out · lower a bid'],
      ['rt', 'Zoom in · raise a bid'],
      ['back', 'Standings'],
      ['start', 'Pause menu'],
    ];
    for (const [button, label] of controls) {
      buttonGlyph(ctx, button, rx + (button.length === 1 ? 13 : 22), rightY + 8, 11);
      text(ctx, label, rx + 56, rightY + 8, {
        size: TYPE.label, weight: 600, color: INK.textDim, baseline: 'middle',
      });
      rightY += 28;
    }

    rightY += 14;
    rightY += this.helpHeading(ctx, 'KEYBOARD (4 PLAYERS, ONE BOARD)', rx, rightY);
    const schemes: [string, string][] = [
      ['P1', 'WASD · F confirm · G back · R menu · T inspect'],
      ['P2', 'Arrows · . confirm · , back · / menu · ; inspect'],
      ['P3', 'IJKL · H confirm · N back · Y menu · U inspect'],
      ['P4', 'Numpad 8456 · 0 confirm · . back · 7 menu · 9 inspect'],
    ];
    for (const [seat, keys] of schemes) {
      text(ctx, seat, rx, rightY + 7, {
        size: TYPE.micro, weight: 800, color: INK.info, baseline: 'middle',
      });
      rightY += paragraph(ctx, keys, rx + 32, rightY, colW - 32, {
        size: TYPE.micro, color: INK.textDim, lineHeight: 16,
      }) + 8;
    }
    void h;
  }

  private helpHeading(ctx: CanvasRenderingContext2D, label: string, x: number, y: number): number {
    text(ctx, label, x, y + 8, {
      size: TYPE.label, weight: 800, color: INK.text, baseline: 'middle', tracking: 2.4,
    });
    ctx.save();
    ctx.strokeStyle = INK.edge;
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(x, y + 22);
    ctx.lineTo(x + 260, y + 22);
    ctx.stroke();
    ctx.restore();
    return 38;
  }

  /* --------------------------------------------------------------------- pause */

  drawPause(ctx: CanvasRenderingContext2D, view: PauseView, w: number, h: number): void {
    ctx.save();
    ctx.fillStyle = 'rgba(4, 5, 10, 0.86)';
    ctx.fillRect(0, 0, w, h);
    ctx.restore();

    const menuW = 400;
    const rowH = 52;
    const menuH = view.items.length * rowH + 132;
    const x = w / 2 - menuW / 2;
    const y = h / 2 - menuH / 2;

    panel(ctx, x, y, menuW, menuH, {
      fill: 'rgba(10, 12, 22, 0.97)',
      border: INK.edgeStrong,
      borderWidth: 2,
      chamfer: 20,
    });

    text(ctx, 'PAUSED', x + menuW / 2, y + 46, {
      size: TYPE.heading, weight: 800, color: INK.text, align: 'center', baseline: 'middle',
      tracking: 5,
    });

    const labels: Record<PauseItem, string> = {
      resume: 'Resume match',
      help: 'How to play',
      audio: view.audio.muted ? 'Audio: muted' : `Audio: ${Math.round(view.audio.master * 100)}%`,
      restart: 'Restart match',
      quit: 'Quit to title',
    };

    view.items.forEach((item, index) => {
      const rowY = y + 84 + index * rowH;
      const selected = index === view.index;

      if (selected) {
        roundRect(ctx, x + 20, rowY, menuW - 40, rowH - 8, 10);
        ctx.fillStyle = withAlpha(INK.info, 0.2);
        ctx.fill();
        ctx.strokeStyle = withAlpha(INK.info, 0.8);
        ctx.lineWidth = 1.6;
        ctx.stroke();
      }

      text(ctx, labels[item], x + menuW / 2, rowY + (rowH - 8) / 2, {
        size: TYPE.body, weight: 700,
        color: selected ? INK.text : INK.textDim,
        align: 'center', baseline: 'middle',
      });

      // The audio row doubles as a slider.
      if (item === 'audio' && selected) {
        text(ctx, '‹ LT / RT ›', x + menuW / 2, rowY + rowH + 4, {
          size: TYPE.micro, weight: 700, color: INK.textFaint, align: 'center', baseline: 'middle',
        });
      }
    });

    hintBar(
      ctx,
      [{ button: 'a', label: 'Select' }, { button: 'start', label: 'Resume' }],
      x + 40, y + menuH - 26,
    );
  }
}
